import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Skeleton,
  Space,
  Typography,
} from "antd";
import {
  EditOutlined,
  GlobalOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  SkinOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import { olreadiaService } from "services/olreadiaService";
import {
  buildOviziaHandoffUrl,
  buildOviziaWebsiteCreationUrl,
} from "./WebsiteCreationRedirect";
import {
  contentSummary,
  formatDate,
  valueOrDash,
  websiteOwnerTypeForWorkspace,
} from "./websiteUtils";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;
const PENDING_WEBSITE_ACTION_KEY = "care:pendingWebsiteAction";
const POLL_INTERVAL_MS = 5000;
const PENDING_TIMEOUT_MS = 10 * 60 * 1000;

const readPendingWebsiteAction = () => {
  try {
    return JSON.parse(
      localStorage.getItem(PENDING_WEBSITE_ACTION_KEY) || "null",
    );
  } catch {
    return null;
  }
};

const writePendingWebsiteAction = (action) => {
  localStorage.setItem(PENDING_WEBSITE_ACTION_KEY, JSON.stringify(action));
};

const clearPendingWebsiteAction = () => {
  localStorage.removeItem(PENDING_WEBSITE_ACTION_KEY);
};

const openInNewTab = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const websiteActionCompleted = (pendingAction, result) => {
  const nextDetails = result?.details;
  const nextWebsite = nextDetails?.website;
  const nextDomains = result?.domains;
  const domainList = Array.isArray(nextDomains)
    ? nextDomains
    : nextDomains?.domains || [];

  if (!pendingAction) return false;
  if (pendingAction.action === "create") return Boolean(nextWebsite);
  if (pendingAction.action === "delete") return !nextWebsite;
  if (pendingAction.action === "domain") {
    return Boolean(
      nextDomains?.hasRegisteredDomains ||
        domainList.some((domain) => domain.status === "ACTIVE") ||
        domainList.length,
    );
  }

  if (!nextWebsite) return false;
  return Boolean(
    (pendingAction.lastWebsiteUpdatedAt &&
      nextWebsite.updatedAt &&
      nextWebsite.updatedAt !== pendingAction.lastWebsiteUpdatedAt) ||
      (pendingAction.lastContentUpdatedAt &&
        nextDetails?.activeContent?.updatedAt &&
        nextDetails.activeContent.updatedAt !==
          pendingAction.lastContentUpdatedAt),
  );
};

export default function WebsiteDetails() {
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, isOrganizationWorkspace } =
    useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const ownerType = websiteOwnerTypeForWorkspace({
    isOrganizationWorkspace,
    selectedWorkspace,
  });
  const isPortfolio = ownerType === "PORTFOLIO";
  const [details, setDetails] = useState(null);
  const [domains, setDomains] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState(readPendingWebsiteAction);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!actorId || !selectedWorkspaceId) return;
    try {
      if (!silent) setLoading(true);
      setError("");
      const next = isPortfolio
        ? await olreadiaService.websites.detailsByUserPortfolio(
            selectedWorkspaceId,
            actorId,
          )
        : await olreadiaService.websites.detailsByWorkspace(
            selectedWorkspaceId,
            actorId,
          );
      setDetails(next);
      let nextDomains = null;
      if (next?.website && !isPortfolio) {
        nextDomains = await olreadiaService.domains
          .ownedDomains(selectedWorkspaceId, actorId)
          .catch(() => null);
        setDomains(nextDomains);
      } else {
        setDomains(null);
      }
      return { details: next, domains: nextDomains };
    } catch (nextError) {
      if (!silent) {
        setError(nextError?.message || "Could not load website details.");
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [actorId, isPortfolio, selectedWorkspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const website = details?.website;
  const summary = useMemo(() => contentSummary(details), [details]);

  const activeDomain =
    domains?.find((domain) => domain.status === "ACTIVE") ||
    domains?.domains?.[0];

  const previewUrl = activeDomain?.domainName
    ? `https://${activeDomain.domainName}`
    : olreadiaService.websites.previewUrl(website?.slug);

  const markPendingAction = (action, currentWebsite = website) => {
    const nextPendingAction = {
      action,
      workspaceId: selectedWorkspaceId,
      ownerType,
      websiteId: currentWebsite?.id || "",
      lastWebsiteUpdatedAt: currentWebsite?.updatedAt || "",
      lastContentUpdatedAt: details?.activeContent?.updatedAt || "",
      startedAt: Date.now(),
    };
    writePendingWebsiteAction(nextPendingAction);
    setPendingAction(nextPendingAction);
  };

  const clearPendingAction = useCallback(() => {
    clearPendingWebsiteAction();
    setPendingAction(null);
  }, []);

  const startWebsiteCreation = () => {
    markPendingAction("create", null);
    openInNewTab(
      buildOviziaWebsiteCreationUrl({
        ownerType,
        selectedWorkspaceId,
      }),
    );
  };

  const openOviziaWebsiteAction = (targetPath, action) => {
    markPendingAction(action);
    openInNewTab(
      buildOviziaHandoffUrl({
        ownerType,
        selectedWorkspaceId,
        targetPath,
      }),
    );
  };

  useEffect(() => {
    const stored = readPendingWebsiteAction();
    if (stored?.workspaceId === selectedWorkspaceId) {
      setPendingAction(stored);
    } else {
      setPendingAction(null);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    if (!pendingAction || pendingAction.workspaceId !== selectedWorkspaceId) {
      return undefined;
    }
    if (!actorId || !selectedWorkspaceId) return undefined;

    let cancelled = false;

    const poll = async () => {
      if (Date.now() - pendingAction.startedAt > PENDING_TIMEOUT_MS) {
        clearPendingAction();
        return;
      }

      const result = await load({ silent: true });
      if (cancelled) return;
      if (websiteActionCompleted(pendingAction, result)) {
        clearPendingAction();
      }
    };

    poll();
    const timer = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [actorId, clearPendingAction, load, pendingAction, selectedWorkspaceId]);

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon">
              <GlobalOutlined />
            </span>
            <div>
              <Text className="olreadia-flow-eyebrow">Website</Text>
              <Title level={2}>Website Details</Title>
              <Paragraph>
                Create or manage a{" "}
                {isPortfolio
                  ? "personal portfolio"
                  : "promotional care facility"}{" "}
                website for {selectedWorkspace?.name || "this workspace"}.
              </Paragraph>
            </div>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
              Refresh
            </Button>
          </section>

          {error ? (
            <Alert
              type="error"
              showIcon
              title="Unable to load website"
              description={error}
              style={{ marginBottom: 12 }}
            />
          ) : null}
          {pendingAction?.workspaceId === selectedWorkspaceId ? (
            <Alert
              type="info"
              showIcon
              title="Watching for Ovizia changes"
              description="Keep this tab open. Care will refresh this page automatically when the website action finishes in Ovizia."
              action={
                <Button size="small" onClick={clearPendingAction}>
                  Stop watching
                </Button>
              }
              style={{ marginBottom: 12 }}
            />
          ) : null}
          {loading && !details ? (
            <Card className="olreadia-flow-card">
              <Skeleton active paragraph={{ rows: 5 }} />
            </Card>
          ) : null}

          {!loading && !website ? (
            <Card className="olreadia-flow-card">
              <Empty description="No website yet">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={startWebsiteCreation}
                >
                  Create Website
                </Button>
              </Empty>
            </Card>
          ) : null}

          {website ? (
            <Space orientation="vertical" size={12} style={{ width: "100%" }}>
              <Card className="olreadia-flow-card">
                <div className="olreadia-flow-card__top">
                  <span className="olreadia-flow-card__icon">
                    <GlobalOutlined />
                  </span>
                  <div>
                    <Title level={4}>
                      {valueOrDash(website.name || summary.headline)}
                    </Title>
                    <Text>{previewUrl}</Text>
                  </div>
                </div>
                <div className="olreadia-flow-actions">
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    href={previewUrl}
                    target="_blank"
                    disabled={!previewUrl}
                  >
                    Open Website
                  </Button>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() =>
                      openOviziaWebsiteAction(
                        `/editor/${encodeURIComponent(website.id)}?panel=website-details`,
                        "update",
                      )
                    }
                  >
                    Update Website Info
                  </Button>
                  <Button
                    icon={<SkinOutlined />}
                    onClick={() =>
                      openOviziaWebsiteAction(
                        `/editor/${encodeURIComponent(website.id)}?panel=templates`,
                        "template",
                      )
                    }
                  >
                    Update Template
                  </Button>
                  {!domains?.hasRegisteredDomains ? (
                    <Button
                      icon={<GlobalOutlined />}
                      onClick={() =>
                        openOviziaWebsiteAction(
                          `/editor/${encodeURIComponent(website.id)}?panel=domains`,
                          "domain",
                        )
                      }
                    >
                      Custom Domain
                    </Button>
                  ) : null}
                </div>
              </Card>

              <Card className="olreadia-flow-card">
                <Title level={4}>Website Info</Title>
                <div className="olreadia-flow-detail-grid">
                  <div>
                    <Text>Tagline</Text>
                    <strong>
                      {valueOrDash(summary.tagline || website.tagline)}
                    </strong>
                  </div>
                  <div>
                    <Text>Template</Text>
                    <strong>{valueOrDash(website.template)}</strong>
                  </div>
                  <div>
                    <Text>Phone</Text>
                    <strong>{valueOrDash(summary.phone)}</strong>
                  </div>
                  <div>
                    <Text>Email</Text>
                    <strong>{valueOrDash(summary.email)}</strong>
                  </div>
                  <div>
                    <Text>City</Text>
                    <strong>{valueOrDash(summary.city)}</strong>
                  </div>
                  <div>
                    <Text>Created</Text>
                    <strong>{formatDate(website.createdAt)}</strong>
                  </div>
                  <div>
                    <Text>Updated</Text>
                    <strong>{formatDate(website.updatedAt)}</strong>
                  </div>
                  <div>
                    <Text>Published</Text>
                    <strong>{formatDate(website.publishedAt)}</strong>
                  </div>
                </div>
              </Card>

              {/* danger zone card */}
              <Card
                className="olreadia-flow-card"
                style={{
                  borderColor: "rgba(239,68,68,0.25)",
                  background: "#fef2f2",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#ef4444",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 14,
                  }}
                >
                  Danger zone
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "14px 16px",
                    borderRadius: 8,
                    border: "0.5px solid rgba(239,68,68,0.25)",
                    background: "#fff",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1a1a1a",
                        marginBottom: 3,
                      }}
                    >
                      Delete this website
                    </div>
                    <div style={{ fontSize: 12, color: "#6b6b6b" }}>
                      Permanently removes the website, all content, and
                      deployments. Continue in Ovizia to confirm this action.
                    </div>
                  </div>
                  <Button
                    danger
                    icon={
                      <i className="ti ti-trash" style={{ fontSize: 13 }} />
                    }
                    onClick={() =>
                      openOviziaWebsiteAction(
                        `/websites/${encodeURIComponent(website.id)}`,
                        "delete",
                      )
                    }
                    style={{ flexShrink: 0 }}
                  >
                    Delete in Ovizia
                  </Button>
                </div>
              </Card>
            </Space>
          ) : null}
        </main>
        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>

    </div>
  );
}
